import { useState } from "react";
import { Sparkles, Pencil, MapPin, Type, Images, Link2, Check, Loader2, ChevronLeft, ChevronRight, LayoutGrid, MoveHorizontal, RectangleHorizontal, RectangleVertical } from "lucide-react";
import { toast } from "sonner";
import api, { imageUrl } from "@/lib/api";

const THUMB_FONTS = [
  { key: "anton", label: "Bold Impact" },
  { key: "bebas", label: "Tall Condensed" },
  { key: "oswald", label: "Condensed Sans" },
  { key: "montserrat", label: "Clean Geometric" },
  { key: "poppins", label: "Modern Rounded" },
  { key: "playfair", label: "Elegant Serif" },
  { key: "pacifico", label: "Handwritten Script" },
  { key: "righteous", label: "Retro Tech" },
  { key: "audiowide", label: "Futuristic Tech" },
  { key: "teko", label: "Sport Condensed" },
  { key: "cinzel", label: "Classic Roman" },
  { key: "rakkas", label: "Arabic Flair" },
  { key: "lalezar", label: "Persian Display" },
  { key: "lobster", label: "Retro Script" },
  { key: "bungee", label: "Urban Display" },
  { key: "alfaslab", label: "Heavy Slab" },
  { key: "passion", label: "Bold Poster" },
];

const THUMB_PLACEMENTS = [
  { key: "top", label: "Top" },
  { key: "top-left", label: "Top-Left" },
  { key: "middle", label: "Middle" },
  { key: "bottom", label: "Bottom" },
  { key: "bottom-left", label: "Bottom-Left" },
  { key: "left", label: "Left-Half" },
  { key: "right", label: "Right-Half" },
];

const THUMB_COLORS = [
  { label: "White", value: "#ffffff" },
  { label: "Black", value: "#111111" },
  { label: "Yellow", value: "#ffd21e" },
  { label: "Red", value: "#e11d2a" },
  { label: "Blue", value: "#1e6fff" },
  { label: "Green", value: "#22c55e" },
  { label: "Pink", value: "#ff2d9b" },
  { label: "Orange", value: "#ff7a1a" },
  { label: "Purple", value: "#8b2fd6" },
  { label: "Gold", value: "#d4af37" },
  { label: "Grey", value: "#6b7280" },
  { label: "Brown", value: "#6b4a2b" },
  { label: "Slate", value: "#475569" },
  { label: "Teal", value: "#0d9488" },
];

// Gradient title fills — rendered server-side onto the letters. Title-only (not for outlines).
const THUMB_GRADIENTS = [
  { label: "Chrome", value: "grad:chrome", css: "linear-gradient(180deg,#6eaadc 0%,#cfe0f0 48%,#7a5f3c 56%,#5a4126 100%)" },
  { label: "Sunset", value: "grad:sunset", css: "linear-gradient(180deg,#ffaa1e 0%,#ff2d9b 55%,#8b2fd6 100%)" },
];

// Full FREE thumbnail overlay editor (font, color, placement incl. left/right, size, behind-host).
// photos: [{id}] source step photos. onUse(image_id) is called when the user keeps a composed thumbnail.
// dark: renders in the app's Black Render aesthetic (green bg, black panels, green outlines, white text).
export function ThumbnailComposer({ photos = [], defaultTitle = "", onUse, dark = false, portrait: portraitDefault = false }) {
  const [sourceId, setSourceId] = useState(photos[0]?.id || null);
  const [expanded, setExpanded] = useState(false);
  const [portrait, setPortrait] = useState(!!portraitDefault);
  const [title, setTitle] = useState(defaultTitle || "");
  const [font, setFont] = useState("anton");
  const [color, setColor] = useState("#ffffff");
  const [customColor, setCustomColor] = useState("#ff5a1f");
  const [outline, setOutline] = useState(false);
  const [outlineColor, setOutlineColor] = useState("#000000");
  const [outlineCustom, setOutlineCustom] = useState("#1e3aaa");
  const [placement, setPlacement] = useState("bottom");
  const [size, setSize] = useState("md");
  const [imgShift, setImgShift] = useState("");   // "" | left | right — pan subject toward one half
  const [url, setUrl] = useState("");
  const [urlColor, setUrlColor] = useState("white");
  const [urlCustom, setUrlCustom] = useState("#ffffff");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  // Theme tokens — light (video editor) vs. dark (Black Render aesthetic on the Podcast editor).
  const iconGreen = "#5d8a52";
  const labelCls = dark ? "text-white" : "text-[#171426]";
  const helpCls = dark ? "text-white/45" : "text-[#9a96ac]";
  const fieldBorder = dark ? "#5d8a52" : "#171426";
  const swIdle = dark ? "#3f5a38" : "#d8d2ea";
  const swActive = dark ? "#ffffff" : "#171426";
  const selectCls = dark
    ? "w-full rounded-xl px-3 py-2.5 text-sm font-bold text-white bg-black cursor-pointer outline-none"
    : "w-full rounded-xl px-3 py-2.5 text-sm font-bold text-[#171426] bg-white cursor-pointer";
  const textFieldCls = dark
    ? "w-full rounded-xl px-3 py-2.5 text-sm text-white bg-black placeholder:text-white/35 outline-none"
    : "w-full rounded-xl px-3 py-2.5 text-sm text-[#171426] bg-white placeholder:text-[#b7b3c6]";
  const titleFieldCls = dark
    ? "w-full rounded-xl px-4 py-2.5 text-sm text-white bg-black placeholder:text-white/35 outline-none"
    : "sms-input w-full px-4 py-2.5 text-sm";
  const sizeActiveStyle = dark
    ? { background: "#4f7746", color: "#fff", border: "2px solid #a9d79b" }
    : { background: "#4f7746", color: "#fff", border: "2px solid #171426" };
  const sizeIdleStyle = dark
    ? { background: "#0f0f16", color: "#fff", border: "2px solid #5d8a52" }
    : { background: "#fff", color: "#171426", border: "2px solid #171426" };
  const backBtnCls = dark
    ? "w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold text-[#a9d79b] bg-[#5d8a52]/15 hover:bg-[#5d8a52]/25 rounded-full py-2 transition-colors"
    : "w-full inline-flex items-center justify-center gap-1.5 text-sm font-bold text-[#4f7746] bg-[#eef3ea] hover:bg-[#e3ebdd] rounded-full py-2 transition-colors";

  const reset = () => setPreview(null);

  const build = async () => {
    if (!sourceId) { toast.error("Pick one of your photos first"); return; }
    if (!title.trim()) { toast.error("Type your title first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/thumbnails/compose", {
        source_image_id: sourceId,
        title: title.trim(),
        title_font: font,
        title_color: color === "custom" ? customColor : color,
        title_placement: placement,
        title_outline: outline,
        title_outline_color: outlineColor === "custom" ? outlineCustom : outlineColor,
        title_size: size,
        img_shift: imgShift,
        website_url: url.trim(),
        url_color: urlColor === "custom" ? urlCustom : urlColor,
        portrait,
      });
      if (!data.image_id) { toast.error("Couldn't build the thumbnail — try again"); return; }
      setPreview(data.image_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the thumbnail. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const use = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      await onUse?.(preview);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid="thumbnail-composer">
      {/* Thumbnail shape — switches the preview + composed output between 16:9 and 9:16 (Shorts/Reels) */}
      <div data-testid="composer-aspect-picker">
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <RectangleVertical size={13} style={{ color: iconGreen }} /> Thumbnail Shape
        </label>
        <div className="flex gap-1.5">
          {[["land", "Landscape 16:9", RectangleHorizontal], ["port", "Portrait 9:16", RectangleVertical]].map(([k, lbl, Icon]) => {
            const isP = k === "port";
            const active = portrait === isP;
            return (
              <button
                key={k}
                type="button"
                data-testid={`composer-aspect-${k}`}
                onClick={() => { setPortrait(isP); reset(); }}
                className="flex-1 rounded-xl px-2 py-2 text-[11px] sm:text-xs font-extrabold text-center transition-colors inline-flex items-center justify-center gap-1.5"
                style={active ? sizeActiveStyle : sizeIdleStyle}
              >
                <Icon size={14} /> {lbl}
              </button>
            );
          })}
        </div>
      </div>

      {/* Source photo picker */}
      <div data-testid="composer-image-picker">
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1.5 px-0.5`}>
          <Images size={13} style={{ color: iconGreen }} /> Choose which photo to use
        </label>
        {photos.length === 0 ? (
          <p className={`text-[11px] ${helpCls} px-0.5`}>No step photos found for this video.</p>
        ) : expanded ? (
          (() => {
            const cur = Math.max(0, photos.findIndex((p) => p.id === sourceId));
            const go = (dir) => { const n = (cur + dir + photos.length) % photos.length; setSourceId(photos[n].id); reset(); };
            return (
              <div className="space-y-2" data-testid="composer-expanded-preview">
                <div className={`relative rounded-xl overflow-hidden border-2 border-[#4f7746] bg-black ${portrait ? "mx-auto max-w-[220px]" : ""}`} style={{ aspectRatio: portrait ? "9 / 16" : "16 / 9" }}>
                  <img src={imageUrl(sourceId)} alt={`Photo ${cur + 1}`} className="w-full h-full object-cover" />
                  <span className="absolute top-2 left-2 text-[11px] font-extrabold text-white bg-black/60 rounded px-1.5 py-0.5">Photo {cur + 1} of {photos.length}</span>
                  {photos.length > 1 && (
                    <>
                      <button type="button" data-testid="composer-prev-photo" onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"><ChevronLeft size={20} /></button>
                      <button type="button" data-testid="composer-next-photo" onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"><ChevronRight size={20} /></button>
                    </>
                  )}
                </div>
                <button type="button" data-testid="composer-gallery-return" onClick={() => setExpanded(false)} className={backBtnCls}>
                  <LayoutGrid size={15} /> Back to all photos
                </button>
              </div>
            );
          })()
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => {
                const active = sourceId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`composer-source-${i}`}
                    onClick={() => { setSourceId(p.id); setExpanded(true); reset(); }}
                    className="relative rounded-lg overflow-hidden transition-transform hover:scale-105"
                    style={{ width: 62, height: 40, border: active ? "3px solid #4f7746" : `2px solid ${swIdle}` }}
                    title={`Tap to preview photo ${i + 1}`}
                  >
                    <img src={imageUrl(p.id)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                    <span className="absolute top-0 left-0 text-[10px] font-extrabold text-white bg-black/60 rounded-br px-1 leading-tight">{i + 1}</span>
                    {active && <span className="absolute inset-0 flex items-center justify-center bg-[#4f7746]/35"><Check size={16} className="text-white" /></span>}
                  </button>
                );
              })}
            </div>
            <p className={`text-[11px] ${helpCls} mt-1 px-0.5`}>Tap any photo to preview it larger.</p>
          </>
        )}
      </div>

      {/* Title */}
      <div>
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <Type size={13} style={{ color: iconGreen }} /> Title
        </label>
        <input
          data-testid="composer-title-input"
          value={title}
          onChange={(e) => { setTitle(e.target.value); reset(); }}
          maxLength={120}
          placeholder="e.g. How to make Apple Pie from Scratch"
          className={titleFieldCls}
          style={{ border: `2px solid ${fieldBorder}` }}
        />
      </div>

      {/* Font */}
      <div>
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <Pencil size={13} style={{ color: iconGreen }} /> Title Font
        </label>
        <select
          data-testid="composer-font-select"
          value={font}
          onChange={(e) => { setFont(e.target.value); reset(); }}
          className={selectCls}
          style={{ border: `2px solid ${fieldBorder}` }}
        >
          {THUMB_FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </div>

      {/* Color */}
      <div data-testid="composer-color-picker">
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1.5 px-0.5`}>
          <Sparkles size={13} style={{ color: iconGreen }} /> Title Color
        </label>
        <div className="flex flex-wrap gap-2">
          {THUMB_COLORS.map((c) => {
            const active = color === c.value;
            return (
              <button
                key={c.value}
                type="button"
                data-testid={`composer-color-${c.label.toLowerCase()}`}
                title={c.label}
                onClick={() => { setColor(c.value); reset(); }}
                className="relative w-8 h-8 rounded-full transition-transform hover:scale-110"
                style={{ background: c.value, border: active ? `3px solid ${swActive}` : `2px solid ${swIdle}` }}
              >
                {active && <Check size={14} className="absolute inset-0 m-auto" style={{ color: (c.label === "White" || c.label === "Yellow" || c.label === "Gold") ? "#171426" : "#fff" }} />}
              </button>
            );
          })}
          {THUMB_GRADIENTS.map((g) => {
            const active = color === g.value;
            return (
              <button
                key={g.value}
                type="button"
                data-testid={`composer-color-${g.label.toLowerCase()}`}
                title={g.label}
                onClick={() => { setColor(g.value); reset(); }}
                className="relative w-8 h-8 rounded-full transition-transform hover:scale-110"
                style={{ background: g.css, border: active ? `3px solid ${swActive}` : `2px solid ${swIdle}` }}
              >
                {active && <Check size={14} className="absolute inset-0 m-auto text-white" />}
              </button>
            );
          })}
          <label
            data-testid="composer-color-custom"
            title="Custom color"
            className="relative w-8 h-8 rounded-full cursor-pointer overflow-hidden flex items-center justify-center"
            style={{ background: customColor, border: color === "custom" ? `3px solid ${swActive}` : `2px solid ${swIdle}` }}
          >
            <Pencil size={12} style={{ color: "#fff", mixBlendMode: "difference" }} />
            <input type="color" value={customColor} onChange={(e) => { setCustomColor(e.target.value); setColor("custom"); reset(); }} className="absolute inset-0 opacity-0 cursor-pointer" />
          </label>
        </div>
      </div>

      {/* Placement — dialed in before the outline */}
      <div>
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <MapPin size={13} style={{ color: iconGreen }} /> Title Placement
        </label>
        <select
          data-testid="composer-placement-select"
          value={placement}
          onChange={(e) => { setPlacement(e.target.value); reset(); }}
          className={selectCls}
          style={{ border: `2px solid ${fieldBorder}` }}
        >
          {THUMB_PLACEMENTS.map((pl) => <option key={pl.key} value={pl.key}>{pl.label}</option>)}
        </select>
        <p className={`text-[11px] ${dark ? "text-white/90" : "text-[#171426]"} mt-1 px-0.5`}>Top, middle, bottom, or <b>Left-Half / Right-Half</b> — keeps the title in half the frame so it wraps to the next line. Great when your host is centered in the shot.</p>
      </div>

      {/* Font outline — always open, like the color picker; pick "Off" to turn it off */}
      <div data-testid="composer-outline-block">
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <Pencil size={13} style={{ color: iconGreen }} /> Font Outline
        </label>
        <p className={`text-[11px] ${dark ? "text-white/70" : "text-[#171426]"} mb-1.5 px-0.5`}>Adds a bold outline around every letter so the title pops off busy photos. Pick <b>Off</b> for no outline.</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="composer-outline-none"
            title="No outline"
            onClick={() => { setOutline(false); reset(); }}
            className="relative w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: dark ? "#0a0a0f" : "#fff", border: !outline ? `3px solid ${swActive}` : `2px solid ${swIdle}` }}
          >
            <span className="text-[10px] font-bold" style={{ color: dark ? "#cfe0c6" : "#171426" }}>Off</span>
          </button>
          {THUMB_COLORS.map((c) => {
            const active = outline && outlineColor === c.value;
            return (
              <button
                key={c.value}
                type="button"
                data-testid={`composer-outline-color-${c.label.toLowerCase()}`}
                title={c.label}
                onClick={() => { setOutline(true); setOutlineColor(c.value); reset(); }}
                className="relative w-8 h-8 rounded-full transition-transform hover:scale-110"
                style={{ background: c.value, border: active ? `3px solid ${swActive}` : `2px solid ${swIdle}` }}
              >
                {active && <Check size={14} className="absolute inset-0 m-auto" style={{ color: (c.label === "White" || c.label === "Yellow" || c.label === "Gold") ? "#171426" : "#fff" }} />}
              </button>
            );
          })}
          <label
            data-testid="composer-outline-color-custom"
            title="Custom outline color"
            className="relative w-8 h-8 rounded-full cursor-pointer overflow-hidden flex items-center justify-center"
            style={{ background: outlineCustom, border: (outline && outlineColor === "custom") ? `3px solid ${swActive}` : `2px solid ${swIdle}` }}
          >
            <Pencil size={12} style={{ color: "#fff", mixBlendMode: "difference" }} />
            <input type="color" value={outlineCustom} onChange={(e) => { setOutlineCustom(e.target.value); setOutlineColor("custom"); setOutline(true); reset(); }} className="absolute inset-0 opacity-0 cursor-pointer" />
          </label>
        </div>
      </div>

      {/* Size */}
      <div data-testid="composer-size-picker">
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <Type size={13} style={{ color: iconGreen }} /> Title Size
        </label>
        <div className="flex gap-1.5">
          {[["sm", "SMALL"], ["md", "MEDIUM"], ["lg", "LARGE"], ["xl", "EXTRA LARGE"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`composer-size-${key}`}
              onClick={() => { setSize(key); reset(); }}
              className="flex-1 rounded-xl px-1.5 py-2 text-[11px] sm:text-xs font-extrabold leading-tight text-center transition-colors"
              style={size === key ? sizeActiveStyle : sizeIdleStyle}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Nudge subject — one control: tap an arrow to slide the subject that way (tap again to re-center) */}
      <div data-testid="composer-shift-block">
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <MoveHorizontal size={13} style={{ color: iconGreen }} /> Nudge Subject
        </label>
        <p className={`text-[11px] ${dark ? "text-white/90" : "text-[#171426]"} mb-3 px-0.5`}>Putting your title on the <b>Left or Right</b>? Tap an arrow to slide your subject the opposite way and open clean space for the text — their face stays in frame. Tap the lit arrow again to re-center.</p>
        <div className="relative mx-auto my-1" style={{ maxWidth: 300, aspectRatio: "16 / 9" }} data-testid="composer-nudge-control">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/b2e009cf89cf3e3560923abdb1410e0b74a3b44ea8668cc398d2072c3c3286a2.jpeg"
            alt="Nudge control — tap an arrow to shift the subject that direction"
            className="w-full h-full object-cover rounded-xl border border-[#2a2536]"
          />
          {[
            ["up", "▲", "left-1/2 -translate-x-1/2 top-1.5"],
            ["down", "▼", "left-1/2 -translate-x-1/2 bottom-1.5"],
            ["left", "◀", "top-1/2 -translate-y-1/2 left-1.5"],
            ["right", "▶", "top-1/2 -translate-y-1/2 right-1.5"],
          ].map(([val, ar, pos]) => {
            const active = imgShift === val;
            return (
              <button
                key={val}
                type="button"
                data-testid={`composer-shift-${val}`}
                title={`Nudge subject ${val}`}
                onClick={() => { setImgShift(active ? "" : val); reset(); }}
                className={`absolute ${pos} w-9 h-9 rounded-full flex items-center justify-center text-sm shadow-lg transition-transform hover:scale-110`}
                style={active
                  ? { background: "#facc15", color: "#171426", border: "2px solid #fff" }
                  : { background: "#4f7746", color: "#fff", border: "2px solid #a9d79b" }}
              >
                {ar}
              </button>
            );
          })}
        </div>
      </div>

      {/* Website URL */}
      <div>
        <label className={`flex items-center gap-1.5 text-xs font-bold ${labelCls} mb-1 px-0.5`}>
          <Link2 size={13} style={{ color: iconGreen }} /> Add your website URL <span className={`font-medium ${helpCls}`}>(optional)</span>
        </label>
        <input
          type="text"
          data-testid="composer-url-input"
          value={url}
          onChange={(e) => { setUrl(e.target.value); reset(); }}
          maxLength={80}
          placeholder="www.insertyourdomainhere.com"
          className={textFieldCls}
          style={{ border: `2px solid ${fieldBorder}` }}
        />
        {url.trim() && (
          <div className="flex items-center gap-2 mt-2">
            <span className={`text-[11px] font-bold ${labelCls}`}>Text color:</span>
            {[{ k: "white", sw: "#ffffff" }, { k: "black", sw: "#111111" }].map((c) => (
              <button key={c.k} type="button" data-testid={`composer-url-color-${c.k}`} title={c.k} onClick={() => { setUrlColor(c.k); reset(); }} className="w-7 h-7 rounded-full transition-transform hover:scale-110" style={{ background: c.sw, border: urlColor === c.k ? "3px solid #5d8a52" : `2px solid ${swIdle}` }} />
            ))}
            <label data-testid="composer-url-color-custom" title="Custom color" className="relative w-7 h-7 rounded-full cursor-pointer overflow-hidden flex items-center justify-center" style={{ background: urlCustom, border: urlColor === "custom" ? "3px solid #5d8a52" : `2px solid ${swIdle}` }}>
              <Pencil size={11} style={{ color: "#fff", mixBlendMode: "difference" }} />
              <input type="color" value={urlCustom} onChange={(e) => { setUrlCustom(e.target.value); setUrlColor("custom"); reset(); }} className="absolute inset-0 opacity-0 cursor-pointer" />
            </label>
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && (
        <div className={`rounded-xl overflow-hidden border-2 border-[#5d8a52] ${portrait ? "mx-auto max-w-[240px]" : ""}`} data-testid="composer-preview">
          <img src={imageUrl(preview)} alt="Thumbnail preview" className={`w-full object-cover ${portrait ? "aspect-[9/16]" : "aspect-video"}`} />
        </div>
      )}

      {/* Actions */}
      <button
        type="button"
        data-testid="composer-build-btn"
        onClick={build}
        disabled={busy || !sourceId || !title.trim()}
        className="w-full font-extrabold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60 transition-transform hover:scale-[1.01] text-white"
        style={{ background: "#1e3aaa", border: "2px solid #1e3aaa", boxShadow: "0 10px 22px -12px rgba(30,58,170,.85)" }}
      >
        {busy ? <><Loader2 size={17} className="spin" /> Building your preview…</> : <><Sparkles size={17} /> {preview ? "Update Preview" : "Create Thumbnail Preview"}</>}
      </button>
      {preview && (
        <button
          type="button"
          data-testid="composer-use-btn"
          onClick={use}
          disabled={applying}
          className="w-full font-extrabold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60 transition-colors text-white"
          style={{ background: "#4f7746", border: dark ? "2px solid #a9d79b" : "none" }}
        >
          {applying ? <><Loader2 size={17} className="spin" /> Setting…</> : <><Check size={17} /> Use This Thumbnail</>}
        </button>
      )}
    </div>
  );
}

export default ThumbnailComposer;
